import { useUser, useMutation } from '@ztechapps/common.hook'

import type {
  AddChatMessageArgs,
  AddCustomerChatReplyArgs,
  AddLogisticChatMessageArgs,
  AddOrderChatMessageArgs,
  AddPurchaseOrderChatMessageArgs,
  ChatMessage,
  ChatSender,
} from '@ztechapps/common.contract'
import { ChatMessageFormatEnum } from '@ztechapps/common.contract'
import type { DocumentNode, FetchResult } from '@apollo/client'
import { gql } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'

const addChatMessagesFragment = gql`
  fragment addChatMessagesFragment on ChatMessage {
    _id
    plainContent
    content
    chat {
      _id
      externalId
      isNew
    }
    timeStamp
    sender {
      _id
      profile {
        _id
        name
        email
      }
    }
  }
`

const addChatMessageQuery = gql`
  ${addChatMessagesFragment}
  mutation addChatMessage($input: AddChatMessageArgs!) {
    addChatMessage(input: $input) {
      ...addChatMessagesFragment
    }
  }
`

export type UseSendChatMessageProps<TArgs> = {
  query?: DocumentNode
  extraArgs: TArgs
}

export type UseSendChatMessageResult<TArgs extends object = AddChatMessageArgs> = {
  sendChat: (args: TArgs) => Promise<FetchResult<{ addChatMessage: ChatMessage }>>
  loading: boolean
  error: any
}

export function useSendChatMessage<TArgs extends AddChatMessageArgs>(
  { query, extraArgs = {} } = {} as UseSendChatMessageProps<TArgs>
): UseSendChatMessageResult<TArgs> {
  query ??= addChatMessageQuery
  const { user } = useUser()

  const [onSend, , { loading, error }] = useMutation<{ addChatMessage: ChatMessage }, TArgs>({
    query,
  })

  const sendChat = async ({
    format = ChatMessageFormatEnum.html,
    isPublic = true,
    extenalId,
    sender,
    ...rest
  }: TArgs) => {
    //if new conversation the initial sender is the actual recipient, for succeeding it's always the logged-in user
    let actualSender = {
      profile: {
        email: user?.profile?.email,
        name: user?.profile?.name,
      },
    } as ChatSender
    if (isEmpty(extenalId)) {
      actualSender = sender
    }

    return onSend({
      input: {
        isPublic,
        format,
        extenalId,
        sender: actualSender,
        ...rest,
        ...extraArgs,
      } as TArgs,
    })
  }

  return { sendChat, loading, error }
}

const addOrderChatMessageQuery = gql`
  ${addChatMessagesFragment}
  mutation addOrderChatMessage($input: AddOrderChatMessageArgs!) {
    addOrderChatMessage(input: $input) {
      ...addChatMessagesFragment
    }
  }
`

export function useSendOrderChatMessage({ orderId }: { orderId: string }) {
  return useSendChatMessage<AddOrderChatMessageArgs>({
    query: addOrderChatMessageQuery,
    extraArgs: {
      orderId,
    } as AddOrderChatMessageArgs,
  })
}

const addPurchaseOrderChatMessageQuery = gql`
  ${addChatMessagesFragment}
  mutation addPurchaseOrderChatMessage($input: AddPurchaseOrderChatMessageArgs!) {
    addPurchaseOrderChatMessage(input: $input) {
      ...addChatMessagesFragment
    }
  }
`

export function useSendPurchaseOrderChatMessage({ purchaseOrderId }: { purchaseOrderId: string }) {
  return useSendChatMessage<AddPurchaseOrderChatMessageArgs>({
    query: addPurchaseOrderChatMessageQuery,
    extraArgs: {
      purchaseOrderId: purchaseOrderId,
    } as AddPurchaseOrderChatMessageArgs,
  })
}

const addLogisticChatMessageQuery = gql`
  ${addChatMessagesFragment}
  mutation addLogisticChatMessage($input: AddLogisticChatMessageArgs!) {
    addLogisticChatMessage(input: $input) {
      ...addChatMessagesFragment
    }
  }
`

export function useSendLogisticChatMessage({ logisticId }: { logisticId: string }) {
  return useSendChatMessage<AddLogisticChatMessageArgs>({
    query: addLogisticChatMessageQuery,
    extraArgs: {
      logisticId,
    } as AddLogisticChatMessageArgs,
  })
}

const addCustomerChatReplyQuery = gql`
  mutation addCustomerChatReply($input: AddCustomerChatReplyArgs!) {
    addCustomerChatReply(input: $input) {
      _id
    }
  }
`

export const useSendCustomerChatReply = (): UseSendChatMessageResult<AddCustomerChatReplyArgs> =>
  useSendChatMessage<AddCustomerChatReplyArgs>({
    query: addCustomerChatReplyQuery,
    extraArgs: {} as AddCustomerChatReplyArgs,
  })
